import { Form, Head } from '@inertiajs/react'

import Heading from '@/components/heading'
import InputError from '@/components/input-error'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import AppLayout from '@/layouts/app-layout'
import { adminUsersPath, newAdminUserPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'

const breadcrumbs: BreadcrumbItem[] = [
  { title: 'Users', href: adminUsersPath() },
  { title: 'New User', href: newAdminUserPath() },
]

export default function AdminUsersNew() {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="New User" />

      <div className="p-6">
        <Heading title="Add User" description="Create a new user account" />

        <Card className="mt-6 max-w-2xl">
          <CardHeader>
            <CardTitle>User Details</CardTitle>
            <CardDescription>Enter the information for the new user</CardDescription>
          </CardHeader>
          <CardContent>
            <Form method="post" action={adminUsersPath()} className="space-y-6">
              {({ processing, errors }) => (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="user[name]"
                      type="text"
                      required
                      autoFocus
                      placeholder="John Doe"
                    />
                    <InputError messages={errors['user.name']} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="user[email]"
                      type="email"
                      required
                      placeholder="john@you.digital"
                    />
                    <InputError messages={errors['user.email']} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="password">Password *</Label>
                    <Input
                      id="password"
                      name="user[password]"
                      type="password"
                      required
                      placeholder="Minimum 12 characters"
                    />
                    <InputError messages={errors['user.password']} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="password_confirmation">Confirm Password *</Label>
                    <Input
                      id="password_confirmation"
                      name="user[password_confirmation]"
                      type="password"
                      required
                      placeholder="Re-enter password"
                    />
                    <InputError messages={errors['user.password_confirmation']} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="role">Role *</Label>
                    <select
                      id="role"
                      name="user[role]"
                      required
                      className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                    >
                      <option value="admin">Admin</option>
                      <option value="superadmin">Superadmin</option>
                    </select>
                    <InputError messages={errors['user.role']} />
                  </div>

                  {errors.base && (
                    <div className="rounded-md bg-destructive/10 p-3">
                      <p className="text-destructive text-sm">{errors.base}</p>
                    </div>
                  )}

                  <div className="flex justify-end gap-4">
                    <Button type="button" variant="outline" onClick={() => history.back()}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={processing}>
                      {processing && <Spinner />}
                      Create User
                    </Button>
                  </div>
                </>
              )}
            </Form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
